import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import { AppointmentCardProps } from "../types/AppointmentCard.types";
import CardWrapper from "../../../common/cards/CardWrapper/CardWrapper";
import AppointmentCardHeader from "../../../common/cards/AppointmentCardHeader/AppointmentCardHeader";
import AppointmentCardBody from "../../../common/cards/AppointmentCardBody/AppointmentCardBody";
import AppointmentCardFooterBtnsContainer from "ui/appointment/components/appointmentList/cardButtons/AppointmentCardFooterBtns/AppointmentCardFooterBtnsContainer";
import LinkedAppointmentSection from "./LinkedAppointmentSection/LinkedAppointmentSection";
import { formatAddToCalendarURI } from "lib/mobileIntegration/addToCalendar/formatUrl";
import { mobileNavigate } from "lib/routing/navigate/navigate";
import { sxStyles } from "./UpcomingAppointmentCard.styles";
import ShowMoreLessPanel from "lib/components/notice/ShowMoreLessPanel/ShowMoreLessPanel";
import mapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";

import VcpCardContainer from "../VcpCard/VcpCardContainer";
import { useNavigate } from "react-router-dom";
import { PATHS } from "lib/routing";
import IMAGES from "lib/assets/images";
import IconTextButton from "lib/components/buttons/IconTextButton/IconTextButton";
import { useAppSelector } from "lib/redux/hooks";
import { selectAppointments } from "ui/appointment/ducks/selectors";

const UpcomingAppointmentCard = ({ appointment }: AppointmentCardProps) => {
  const theme = useTheme();
  const classes = sxStyles(theme);
  const navigate = useNavigate();

  const {
    appointmentId,
    datetime,
    institutionName,
    location,
    serviceDisplayName,
    clinician,
    departmentName,
    visitType,
    zoneInfo,
    calendarTitle,
    calendarLocation,
    linkedAppointments,
    reminder,
    formFlowDisclaimer,
    regErrorMessage,
    isVirtualConsultAppointment,
    actionMessage,
    canCancel,
    canReschedule,
    institutionCode,
    hasTeleconsultmessage,
  } = appointment;

  const teleconsultMessage =
    useAppSelector(selectAppointments).teleconsultMessage;

  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const isJCH = institutionCode === "JCH";

  const isExceedRescheduleLimit = appointment.exceedRescheduleLimit;

  const isLinkedAppt =
    appointment.linkedAppointments &&
    appointment.linkedAppointments?.length > 0;

  const isCrossInstitution = appointment.isCrossInstitution;

  return (
    <Box>
      {isLinkedAppt ? (
        <Box sx={classes.linkedHeader}>
          <Typography sx={classes.linkedTitle}>Linked Appointments</Typography>
          <Box
            component={"img"}
            aria-label="help"
            src={IMAGES.general.HelpOutline}
            sx={classes.helpIcon}
            onClick={handleClickOpen}
          />
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <IconButton
              aria-label="close"
              sx={classes.closeButton}
              onClick={handleClose}
            >
              <Box component={"img"} src={IMAGES.general.GeneralCloseIcon} />
            </IconButton>
            <DialogTitle id="alert-dialog-title" sx={classes.dialogTitle}>
              {"What are Linked Appointments?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="alert-dialog-description"
                sx={classes.dialogText}
              >
                These are related appointments which your care provider has
                planned for your next visit.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                sx={classes.okButton}
                variant="outlined"
                color="primary"
                fullWidth={true}
                onClick={handleClose}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      ) : null}

      <CardWrapper isLinkedAppointmentStyled={linkedAppointments !== null}>
        <Box sx={classes.cardTopContainer}>
          <AppointmentCardHeader
            datetime={datetime}
            isHighlighted={true}
            isVcp={isVirtualConsultAppointment === true ? true : false}
          />

          <AppointmentCardBody
            institution={institutionName}
            datetime={datetime}
            departmentName={departmentName}
            clinician={clinician}
            location={location}
            zoneInfo={zoneInfo}
            serviceDisplayName={serviceDisplayName}
            visitType={visitType}
            isVcp={isVirtualConsultAppointment === true ? true : false}
          />
          <Box sx={classes.addToCalendarContainer}>
            <IconTextButton
              icon={IMAGES.appointment.AddToCalendarIcon}
              label={["Add to", "calendar"]}
              onClick={() =>
                mobileNavigate(
                  formatAddToCalendarURI(
                    appointmentId,
                    datetime,
                    calendarLocation,
                    institutionName,
                    calendarTitle,
                  ),
                )
              }
              ariaLabel={"Add to calendar"}
            />
          </Box>
        </Box>

        {isVirtualConsultAppointment && (
          <VcpCardContainer appointment={appointment} />
        )}

        {hasTeleconsultmessage && (
          <Box m={1}>
            <ShowMoreLessPanel message={teleconsultMessage} />
          </Box>
        )}

        {reminder && (
          <Box sx={classes.reminderText}>
            <ShowMoreLessPanel message={reminder} showRaw={true} />
          </Box>
        )}

        <Box>
          <LinkedAppointmentSection linkedAppointments={linkedAppointments} />
        </Box>

        {isCrossInstitution
          ? actionMessage && (
              // !exp margin added to compensate for hiding of buttons
              <Typography sx={{ ...classes.actionMsg, m: 1 }}>
                {mapRawStringToReactElement(actionMessage)}
              </Typography>
            )
          : isExceedRescheduleLimit
            ? isJCH
              ? actionMessage && (
                  <Typography sx={classes.actionMsg}>
                    {mapRawStringToReactElement(actionMessage + `.`)}
                  </Typography>
                )
              : actionMessage && (
                  <Typography sx={classes.actionMsg}>
                    {mapRawStringToReactElement(
                      actionMessage +
                        ` or submit your request using below link${
                          !canCancel && !canReschedule ? "s" : ""
                        }.`,
                    )}
                  </Typography>
                )
            : // !exp formFlowDisclaimer value is created from lambda
              formFlowDisclaimer && (
                <Typography sx={classes.shouldSubmitRequestDisclaimerText}>
                  {mapRawStringToReactElement(formFlowDisclaimer)}
                </Typography>
              )}

        <AppointmentCardFooterBtnsContainer
          appointment={appointment}
          onCancelSystemFlow={() => navigate(PATHS.APPOINTMENT_CANCEL.path)}
          isCrossInstitution={isCrossInstitution}
        />

        {regErrorMessage && (
          <Box p={1} sx={classes.reminderText}>
            <ShowMoreLessPanel message={regErrorMessage} />
          </Box>
        )}
      </CardWrapper>
    </Box>
  );
};

export default UpcomingAppointmentCard;
